import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';
import { 
    IconShield, IconAlertTriangle, IconLayers, IconBriefcase
} from '../components/Icons';
import toast from 'react-hot-toast';

// Interfaces
interface Book { id: string; name: string; }
interface Position { ticker: string; market_value: number; }

const RiskManagementView: React.FC = () => {
    const { currentUser } = useAuth();
    
    // UI State
    const [books, setBooks] = useState<Book[]>([]);
    const [selectedBookId, setSelectedBookId] = useState<string>('ALL'); // Default to ALL or specific ID
    const [riskData, setRiskData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [booksLoaded, setBooksLoaded] = useState(false); // Track if books have been loaded

    // 1. Fetch List of Available Books on Mount
    useEffect(() => {
        const loadBooks = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/portfolios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.portfolios) {
                    setBooks(data.portfolios);
                    
                    // Try to restore last used book, otherwise default to first
                    const lastId = localStorage.getItem('aiq_last_book_id');
                    if (lastId && data.portfolios.find((b: any) => b.id === lastId)) {
                        setSelectedBookId(lastId);
                    } else if (data.portfolios.length > 0) {
                        setSelectedBookId(data.portfolios[0].id);
                    }
                }
                setBooksLoaded(true); // Mark books as loaded
            } catch (e) {
                console.error("Failed to load books", e);
                setBooksLoaded(true); // Mark as loaded even on error
            }
        };
        loadBooks();
    }, [currentUser]);

    // 2. Main Logic: Fetch Positions & Run Risk
    useEffect(() => {
        const runAnalysis = async () => {
            if (!currentUser || !selectedBookId) return;
            if (!booksLoaded) return; // Wait for books to load before running analysis
            if (books.length === 0 && selectedBookId !== 'ALL') return;

            setIsLoading(true);
            setRiskData(null); // Clear old data while loading

            try {
                const token = await currentUser.getIdToken();
                let aggregatedPositions: Position[] = [];

                // --- PATH A: AGGREGATE ALL BOOKS ---
                if (selectedBookId === 'ALL') {
                    toast.loading("Aggregating Firm-wide Risk...", { id: 'risk-load' });
                    
                    // Parallel fetch of ALL summaries
                    const promises = books.map(book => 
                        fetch(`${API_BASE_URL}/portfolios/${book.id}/summary`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        }).then(r => r.json())
                    );
                    
                    const results = await Promise.all(promises);
                    
                    // Merge logic: Combine positions with same ticker
                    const merger: Record<string, number> = {};
                    
                    results.forEach((portData: any) => {
                        if (portData && portData.pnl_by_ticker) {
                            portData.pnl_by_ticker.forEach((p: any) => {
                                // Only process valid positions
                                if (p.market_value && Math.abs(p.market_value) > 1) {
                                    merger[p.ticker] = (merger[p.ticker] || 0) + p.market_value;
                                }
                            });
                        }
                    });

                    // Convert map back to array
                    aggregatedPositions = Object.entries(merger).map(([ticker, mv]) => ({
                        ticker, market_value: mv
                    }));

                } 
                // --- PATH B: SINGLE BOOK ---
                else {
                    const bookName = books.find(b => b.id === selectedBookId)?.name || 'Book';
                    toast.loading(`Analyzing ${bookName}...`, { id: 'risk-load' });
                    
                    const portRes = await fetch(`${API_BASE_URL}/portfolios/${selectedBookId}/summary`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const portData = await portRes.json();
                    
                    if (portData && portData.pnl_by_ticker) {
                        aggregatedPositions = portData.pnl_by_ticker
                            .filter((p: any) => Math.abs(p.market_value) > 1)
                            .map((p: any) => ({
                                ticker: p.ticker,
                                market_value: p.market_value
                            }));
                    }
                }

                // Check if we have anything to analyze
                if (aggregatedPositions.length === 0) {
                    toast.error("No positions found to analyze.", { id: 'risk-load' });
                    setIsLoading(false);
                    return;
                }

                // --- CALL RISK ENGINE ---
                // We reuse the existing backend endpoint
                const riskRes = await fetch(`${API_BASE_URL}/risk/analyze`, {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ positions: aggregatedPositions })
                });
                
                if (!riskRes.ok) throw new Error("Risk Engine Error");
                
                const riskJson = await riskRes.json();
                setRiskData(riskJson);
                toast.success("Risk metrics updated", { id: 'risk-load' });

            } catch (e: any) {
                console.error(e);
                toast.error("Analysis failed", { id: 'risk-load' });
            } finally {
                setIsLoading(false);
            }
        };

        runAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, selectedBookId, books.length, booksLoaded]); // Re-run when book changes or books are loaded (books object stable)

    // Helpers
    const handleBookChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newId = e.target.value;
        setSelectedBookId(newId);
        if (newId !== 'ALL') {
            localStorage.setItem('aiq_last_book_id', newId); // Persist selection
        }
    };

    const getBetaColor = (beta: number) => {
        if (beta > 1.3) return 'text-red-500';
        if (beta < 0.7) return 'text-blue-500';
        return 'text-slate-700';
    };

    const getCorrColor = (val: number) => {
        if (val > 0.8) return 'bg-red-500 text-white';
        if (val > 0.5) return 'bg-orange-400 text-white';
        if (val > 0.2) return 'bg-blue-300 text-slate-800';
        if (val > -0.2) return 'bg-slate-100 text-slate-400';
        return 'bg-blue-600 text-white';
    };

    if (isLoading && !riskData) return <div className="flex h-full items-center justify-center text-slate-400 animate-pulse">Running Monte Carlo Simulations...</div>;

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden font-sans">
            
            {/* LEFT: RISK SUMMARY */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconShield className="text-red-600" /> Risk Monitor
                    </h2>
                    <p className="text-xs text-slate-500">Exposures & VaR Analysis</p>
                </div>

                {/* --- BOOK SWITCHER --- */}
                <div className="mb-6">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Scope</label>
                    <div className="relative">
                        <select 
                            value={selectedBookId} 
                            onChange={handleBookChange}
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                        >
                            <option value="ALL">🏢 Aggregate Firm (All Books)</option>
                            <option disabled>──────────</option>
                            {books.map(b => (
                                <option key={b.id} value={b.id}>📘 {b.name}</option>
                            ))}
                        </select>
                        <div className="absolute right-3 top-3 text-slate-400 pointer-events-none">
                            <IconBriefcase size={14} />
                        </div>
                    </div>
                </div>

                {riskData ? (
                    <>
                        {/* BETA GAUGE */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                            <div className="text-xs font-bold text-slate-400 uppercase mb-1">Portfolio Beta (vs SPY)</div>
                            <div className={`text-3xl font-mono font-bold ${getBetaColor(riskData.portfolio_metrics.beta)}`}>
                                {riskData.portfolio_metrics.beta}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">
                                {riskData.portfolio_metrics.beta > 1 ? "Aggressive (High Volatility)" : "Defensive (Low Volatility)"}
                            </p>
                        </div>

                        {/* VaR CARD */}
                        <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
                            <div className="flex items-center gap-2 mb-1">
                                <IconAlertTriangle size={14} className="text-red-500"/>
                                <span className="text-xs font-bold text-red-700 uppercase">Daily VaR (95%)</span>
                            </div>
                            <div className="text-2xl font-mono font-bold text-red-800">
                                ${Math.round(riskData.portfolio_metrics.total_var_95).toLocaleString()}
                            </div>
                            <p className="text-[10px] text-red-600/70 mt-1">
                                Estimated worst-case loss in 1 out of 20 days.
                            </p>
                        </div>

                        {/* HIGHEST VOLATILITY TABLE */}
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Highest Volatility Assets</h3>
                        <div className="space-y-2">
                            {riskData.risk_table
                                .sort((a: any, b: any) => b.volatility - a.volatility)
                                .slice(0, 5)
                                .map((pos: any) => (
                                <div key={pos.ticker} className="flex justify-between items-center p-2 rounded border border-slate-100 bg-white">
                                    <span className="font-bold text-xs text-slate-700">{pos.ticker}</span>
                                    <div className="text-right">
                                        <div className="text-xs font-mono font-bold text-red-600">{pos.volatility}%</div>
                                        <div className="text-[9px] text-slate-400">Ann. Vol</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="text-center py-10 text-slate-400 text-xs">
                        Select a book to view risk metrics.
                    </div>
                )}
            </div>

            {/* RIGHT: CORRELATION MATRIX */}
            <div className="flex-1 p-6 flex flex-col overflow-hidden">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <IconLayers className="text-blue-600"/> Correlation Matrix
                        </h3>
                        <p className="text-xs text-slate-500">
                            Identify concentration risk. <span className="text-red-500 font-bold">Red cells</span> indicate assets moving together.
                        </p>
                    </div>

                    <div className="flex-1 overflow-auto custom-scrollbar">
                        {riskData && riskData.correlation_matrix ? (
                            <div 
                                className="grid gap-px bg-slate-100 border border-slate-200"
                                style={{ 
                                    gridTemplateColumns: `40px repeat(${riskData.correlation_matrix.labels.length}, 1fr)` 
                                }}
                            >
                                {/* Header Row */}
                                <div className="h-8 bg-white"></div>
                                {riskData.correlation_matrix.labels.map((ticker: string) => (
                                    <div key={ticker} className="h-8 flex items-center justify-center text-[10px] font-bold text-slate-500 bg-slate-50">
                                        {ticker}
                                    </div>
                                ))}

                                {/* Data Rows */}
                                {riskData.correlation_matrix.data.map((row: number[], i: number) => (
                                    <React.Fragment key={i}>
                                        {/* Row Label */}
                                        <div className="h-10 flex items-center justify-center text-[10px] font-bold text-slate-500 bg-slate-50">
                                            {riskData.correlation_matrix.labels[i]}
                                        </div>
                                        {/* Cells */}
                                        {row.map((val: number, j: number) => (
                                            <div 
                                                key={`${i}-${j}`} 
                                                className={`h-10 flex items-center justify-center text-[10px] font-mono ${getCorrColor(val)}`}
                                                title={`${riskData.correlation_matrix.labels[i]} vs ${riskData.correlation_matrix.labels[j]}: ${val}`}
                                            >
                                                {val === 1 ? '' : val.toFixed(2)}
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </div>
                        ) : (
                            <div className="flex h-full items-center justify-center text-slate-300">
                                <IconLayers size={64} className="opacity-20" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RiskManagementView;